import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeishuAuthStore } from '@/store/feishu-auth.store';
import { ragApi } from '@/api/rag.api';

type CallbackStatus = 'processing' | 'success' | 'error';

const RAG_BUILD_PATH = '/plugins/rags/build';
const RAG_BUILD_FEISHU_STATE = { uploadMode: 'feishu' as const };

export default function FeishuCallback() {
  const navigate = useNavigate();
  const { setFeishuUserToken, feishuAppSecret } = useFeishuAuthStore();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [message, setMessage] = useState('正在处理飞书授权回调...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const error = params.get('error');
        if (error) {
          setStatus('error');
          setMessage(decodeURIComponent(error));
          setTimeout(
            () => navigate(RAG_BUILD_PATH, { replace: true, state: RAG_BUILD_FEISHU_STATE }),
            1800
          );
          return;
        }
        const code = params.get('code');
        if (!code) {
          setStatus('error');
          setMessage('未接收到授权码');
          setTimeout(
            () => navigate(RAG_BUILD_PATH, { replace: true, state: RAG_BUILD_FEISHU_STATE }),
            1800
          );
          return;
        }

        if (!feishuAppSecret || feishuAppSecret.trim().length === 0) {
          setStatus('error');
          setMessage('飞书应用密钥未配置，请在知识库页面填写后再重新授权');
          setTimeout(
            () => navigate(RAG_BUILD_PATH, { replace: true, state: RAG_BUILD_FEISHU_STATE }),
            1800
          );
          return;
        }

        // Exchange the one-time encrypted code for the real token via POST.
        const resp = await ragApi.exchangeFeishuToken({
          code,
          appSecret: feishuAppSecret,
        });
        const token = resp?.feishu_user_token;
        if (!token) {
          setStatus('error');
          setMessage('授权码交换失败：未获取到 Token');
          setTimeout(
            () => navigate(RAG_BUILD_PATH, { replace: true, state: RAG_BUILD_FEISHU_STATE }),
            1800
          );
          return;
        }

        const expiresIn = resp?.expires_in ?? 0;
        setFeishuUserToken(token, expiresIn);
        setStatus('success');
        setMessage('飞书授权成功，正在跳转...');
        setTimeout(
          () => navigate(RAG_BUILD_PATH, { replace: true, state: RAG_BUILD_FEISHU_STATE }),
          800
        );
      } catch (error) {
        setStatus('error');
        setMessage(error instanceof Error ? error.message : '飞书授权失败');
        setTimeout(
          () => navigate(RAG_BUILD_PATH, { replace: true, state: RAG_BUILD_FEISHU_STATE }),
          1800
        );
      }
    };

    handleCallback();
  }, [navigate, setFeishuUserToken, feishuAppSecret]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="text-center">
        {status === 'processing' && (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
        )}
        {status === 'success' && (
          <div className="rounded-full h-12 w-12 bg-green-100 flex items-center justify-center mx-auto text-green-600">
            ✓
          </div>
        )}
        {status === 'error' && (
          <div className="rounded-full h-12 w-12 bg-red-100 flex items-center justify-center mx-auto text-red-600">
            ✕
          </div>
        )}
        <p className="mt-4 text-gray-600">{message}</p>
      </div>
    </div>
  );
}
